import type { Ref } from 'vue';
import { computed } from 'vue';

import type {
  IAffiliateProgramCommissionFilterErrors,
  IAffiliateProgramCommissionFilterFormWithErrors,
  ICommissionCountryFilterItem,
  ITransactionType,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { useI18n } from '@modules/i18n/composables';

import type { CommissionCountriesTab } from '../../CommissionCountriesTabs/types';
import { isInvalidStep } from './utils';

export interface CommissionCountryFilterWithErrorsComposable {
  countryFiltersWithErrors: Ref<IAffiliateProgramCommissionFilterFormWithErrors[]>;
  activeCountryFilter: Ref<IAffiliateProgramCommissionFilterFormWithErrors | undefined>;
  totalFilterErrors: Ref<IAffiliateProgramCommissionFilterFormWithErrors[] | undefined>;
  displayFilterErrors: Ref<string[] | undefined>;
  countryTabs: Ref<CommissionCountriesTab[]>;
}

export default function useCommissionCountryFilterWithErrors(
  countryFilters: Ref<ICommissionCountryFilterItem[]>,
  activeFilterIndex: Ref<number>,
  firstGroupStepTransactionType: Ref<ITransactionType>,
  secondGroupStepTransactionType: Ref<ITransactionType>,
  transactionType: Ref<string | undefined>,
  selectedProduct: Ref<string | undefined>,
  productOptions: Ref<IFormSelectInputOption<string>[]>,
): CommissionCountryFilterWithErrorsComposable {
  const { getTranslation } = useI18n();

  // eslint-disable-next-line sonarjs/cognitive-complexity
  function getFilterItemErrors(item: ICommissionCountryFilterItem): IAffiliateProgramCommissionFilterErrors {
    const errors: IAffiliateProgramCommissionFilterErrors = {};

    if (!item.isAllCountries && item.value.length === 0) {
      errors.validCountriesSelection = [getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_COUNTRY_REQUIRED').value];
    }

    // Deals only available at CPA
    if (transactionType.value === 'cpa' && item.condition_items_attributes.filter((val) => !val._destroy).length === 0) {
      errors.validItemAttributes = [getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_ITEM_IS_ABSENT').value];
    }

    // First block available for all except revenue_share
    if (transactionType.value !== 'revenue_share') {
      if (item.condition_steps_attributes.filter((step) => (
        step.type_in_part_condition === firstGroupStepTransactionType.value && !step._destroy
      )).length === 0) {
        errors.validFirstGroupSteps = [getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_STEP_IS_ABSENT').value];
      }

      const hasFirstGroupStepErrors = item.condition_steps_attributes.some((step) => (
        isInvalidStep(step, firstGroupStepTransactionType.value)
      ));
      if (hasFirstGroupStepErrors) {
        errors.validFirstGroupStepsPartially = [
          getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_INVALID_SUM_PRODUCT_STEPS').value,
        ];
      }
    }

    // Second block available only for revenue_share and hybrid
    if (transactionType.value === 'revenue_share' || transactionType.value === 'hybrid') {
      if (transactionType.value === 'hybrid' && item.condition_items_attributes.filter((val) => !val._destroy).length === 0) {
        errors.validItemAttributes = [getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_ITEM_IS_ABSENT').value];
      }

      if (item.condition_steps_attributes.filter((step) => (
        step.type_in_part_condition === secondGroupStepTransactionType.value && !step._destroy
      )).length === 0) {
        errors.validSecondGroupSteps = [getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_STEP_IS_ABSENT').value];
      }

      const hasSecondGroupStepErrors = item.condition_steps_attributes.some((step) => (
        isInvalidStep(step, secondGroupStepTransactionType.value)
      ));
      if (hasSecondGroupStepErrors) {
        errors.validSecondGroupStepsPartially = [
          getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_INVALID_SUM_PRODUCT_STEPS').value,
        ];
      }

      if (!errors.validSecondGroupSteps) {
        const hasInvalidProductErrors = item.condition_steps_attributes.some((step) => (
          step.product !== selectedProduct.value && isInvalidStep(step, secondGroupStepTransactionType.value)
        ));

        const notSelectedProducts = productOptions.value
          .filter((product) => product.value !== selectedProduct.value)
          .map((product) => product.label);

        if (hasInvalidProductErrors) {
          errors.validSecondGroupProducts = [
            `${getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_INVALID_SUM_PRODUCT_STEPS').value} 
            ${notSelectedProducts.join(', ')}`,
          ];
        }
      }
    }

    return errors;
  }

  function convertFilterItemToItemWithErrors(item: ICommissionCountryFilterItem): IAffiliateProgramCommissionFilterFormWithErrors {
    const errors: IAffiliateProgramCommissionFilterErrors = item._destroy ? {} : getFilterItemErrors(item);

    return {
      ...item,
      emptyLabel: item.isAllCountries
        ? getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FILTER_ALL_COUNTRIES').value
        : getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FILTER_NO_NAME', {
          id: item.emptyLabelId,
        }).value,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  }

  const countryFiltersWithErrors = computed<IAffiliateProgramCommissionFilterFormWithErrors[]>(() => (
    countryFilters.value.map((item) => convertFilterItemToItemWithErrors(item))
  ));

  const activeCountryFilter = computed<IAffiliateProgramCommissionFilterFormWithErrors | undefined>(() => (
    countryFiltersWithErrors.value[activeFilterIndex.value]
  ));

  const totalFilterErrors = computed<IAffiliateProgramCommissionFilterFormWithErrors[] | undefined>(() => (
    countryFiltersWithErrors.value.filter((item) => !!item.errors)
  ));

  const displayFilterErrors = computed<string[] | undefined>(() => {
    const errorNames = countryFiltersWithErrors.value
      .filter((item, index) => !!item.errors && index !== activeFilterIndex.value)
      .map((item) => item.label || item.emptyLabel);

    return errorNames.length > 0
      ? [`${getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'VALIDATIONS_INVALID_SOME_FILTER').value}: ${errorNames.join(', ')}`]
      : undefined;
  });

  const countryTabs = computed<CommissionCountriesTab[]>(() => {
    const tabs: CommissionCountriesTab[] = [];

    countryFiltersWithErrors.value.forEach((item, index) => {
      if (!item._destroy) {
        tabs.push({
          index,
          label: item.label,
          emptyLabel: item.emptyLabel,
          expanded: item.expanded,
          isActive: index === activeFilterIndex.value,
        });
      }
    });

    return tabs;
  });

  return {
    countryFiltersWithErrors,
    activeCountryFilter,
    totalFilterErrors,
    displayFilterErrors,
    countryTabs,
  };
}
